/**
 * Rides the passenger has closed with «Готово», kept on the device.
 *
 * `useActiveRide` offers every finished ride of the last {@link FINISHED_RIDE_WINDOW}
 * that has not been closed, so a list held in React state alone handed the ride back
 * to the passenger on the next reload — and the one before it after that, and so on
 * until the window had run out on all of them. The Flutter app keeps the same store
 * in its secure storage; see `DismissedNotifier` there.
 *
 * There is nowhere else to put it. The stars are optional, so a passenger who closes
 * the screen without leaving any sends the server nothing at all, and the ride is
 * over on both sides whether they pressed the button or not: nothing over there can
 * say which finished rides this passenger has already dealt with.
 */

const KEY = 'limuzin.customer.closed-rides';

/**
 * How long a ride that is over goes on holding the screen. Past this it is history
 * like any other, «Готово» or no «Готово»: a summary the passenger never closed is
 * worth showing them when they open the app that evening, and not worth showing them
 * the following week.
 */
export const FINISHED_RIDE_WINDOW = 2 * 60 * 60 * 1000;

/**
 * What the store has, less anything closed longer ago than the window — dropped on
 * the way out and never written back, so it holds the handful of rides that could
 * still take the screen rather than every ride this browser has ever finished.
 *
 * A store that cannot be read, or that holds something this version does not
 * understand, is an empty one. The cost of getting that wrong is one summary shown
 * twice; the cost of throwing is a private window — where `localStorage` does not
 * answer — being unable to order a taxi.
 */
function closedRides(): Map<string, number> {
  const kept = new Map<string, number>();
  try {
    const stored = localStorage.getItem(KEY);
    if (stored === null) return kept;
    const since = Date.now() - FINISHED_RIDE_WINDOW;
    const held = JSON.parse(stored) as Record<string, unknown>;
    for (const [rideId, value] of Object.entries(held)) {
      const closedAt = typeof value === 'string' ? Date.parse(value) : NaN;
      if (Number.isNaN(closedAt) || closedAt < since) continue;
      kept.set(rideId, closedAt);
    }
  } catch {
    return new Map();
  }
  return kept;
}

/** What `SessionProvider` starts from, read before the first render. */
export function readClosedRides(): Set<string> {
  return new Set(closedRides().keys());
}

/**
 * Remembers that this ride has been dealt with. Silent on failure, like the install
 * coach's own flag: the ride is closed for this tab either way, and a browser that
 * will not take it is not worth telling the passenger about.
 */
export function writeClosedRide(rideId: string): void {
  const closed = closedRides();
  closed.set(rideId, Date.now());
  try {
    localStorage.setItem(
      KEY,
      JSON.stringify(
        Object.fromEntries(
          [...closed].map(([id, closedAt]) => [id, new Date(closedAt).toISOString()]),
        ),
      ),
    );
  } catch {
    // A private window, or a store that is full.
  }
}
